import { useMemo } from "react";
import { useLocation, Link } from "react-router-dom";
import { useInterlinkRules } from "@/hooks/useInterlinkRules";
import { Link2 } from "lucide-react";

interface RelatedInterlinksProps {
  /** The full text to scan for keyword matches */
  text: string;
  /** Max links to show */
  maxLinks?: number;
}

/**
 * Renders a "Related Content" section by scanning text for interlink keyword matches.
 * Designed for long-form content (articles, descriptions) where inline injection isn't possible.
 */
export const RelatedInterlinks = ({ text, maxLinks = 4 }: RelatedInterlinksProps) => {
  const { data: rules } = useInterlinkRules();
  const location = useLocation();
  const currentPath = location.pathname;

  const matches = useMemo(() => {
    if (!rules || !text) return [];
    const lowerText = text.toLowerCase();
    const seen = new Set<string>();

    return rules
      .filter((r) => r.target_url !== currentPath)
      .sort((a, b) => a.total_incoming - b.total_incoming)
      .filter((r) => {
        if (seen.has(r.target_url)) return false;
        const regex = new RegExp(`\\b${escapeRegex(r.keyword)}\\b`, "i");
        if (!regex.test(lowerText)) return false;
        seen.add(r.target_url);
        return true;
      })
      .slice(0, maxLinks);
  }, [rules, text, currentPath, maxLinks]);

  if (matches.length === 0) return null;

  return (
    <div className="my-8 p-6 bg-muted/50 rounded-xl border">
      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <Link2 className="w-5 h-5 text-primary" />
        Contenido Relacionado
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {matches.map((rule) => (
          <Link
            key={rule.id}
            to={rule.target_url}
            className="flex items-center gap-3 p-3 rounded-lg bg-background hover:bg-accent transition-colors group"
          >
            {rule.target_image && isValidImageUrl(rule.target_image) && (
              <img
                src={rule.target_image}
                alt={rule.target_title || rule.keyword}
                className="w-12 h-12 rounded-md object-cover flex-shrink-0"
                loading="lazy"
              />
            )}
            <div className="min-w-0">
              <p className="font-medium text-sm truncate group-hover:text-primary transition-colors">
                {rule.target_title || rule.keyword}
              </p>
              <p className="text-xs text-muted-foreground truncate">{rule.target_url}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

function escapeRegex(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function isValidImageUrl(url: string): boolean {
  if (!url) return false;
  if (url.startsWith("/src/")) return false;
  if (url.startsWith("/default-")) return false;
  if (url.includes("oaidalleapiprodscus.blob.core.windows.net")) return false;
  return url.startsWith("http") || url.startsWith("/");
}
